import { TEST_LEVEL } from '../config/level';
import { SEED_LAYOUT } from '../config/seed';
import { mulberry32 } from '../utils/prng';
import type { Box, LevelDefinition, SolidSpec } from '../world/Level';
import { isSeed } from './seed';

/** Each object owns its stream: adding an unrelated prop cannot reroll the map. */
function stream(seed: number, key: string): () => number {
  let hash = seed;
  for (let i = 0; i < key.length; i++) hash = Math.imul(hash ^ key.charCodeAt(i), 0x01000193);
  return mulberry32(hash);
}

function choice<T>(seed: number, key: string, choices: readonly T[]): T {
  return choices[Math.floor(stream(seed, key)() * choices.length)];
}

function shuffled<T>(seed: number, key: string, items: readonly T[]): T[] {
  const result = [...items];
  const rng = stream(seed, key);
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function translated(box: Box, dx: number, dz: number): Box {
  return { ...box, minX: box.minX + dx, maxX: box.maxX + dx,
    minZ: box.minZ + dz, maxZ: box.maxZ + dz };
}

/** Move complete car silhouettes, keeping the six authored gap widths and lids. */
function seededParking(seed: number, base: LevelDefinition): Map<string, SolidSpec> {
  const bends = base.solids.flatMap((car) => {
    const match = /^auto-([pe])(\d+)$/.exec(car.name);
    if (!match || !car.boot) return [];
    const twinName = `auto-${match[1] === 'p' ? 'e' : 'p'}${match[2]}`;
    const twin = base.solids.find((other) => other.name === twinName);
    if (!twin) return [];
    const gap = car.boot.end > 0
      ? twin.box.minZ - car.box.maxZ
      : car.box.minZ - twin.box.maxZ;
    return [{ car, twin, gap }];
  }).sort((a, b) => a.car.box.minX - b.car.box.minX);
  const cars = base.solids.filter((solid) => /^auto-[pe]\d+$/.test(solid.name));
  const north = cars.find((car) => car.name === 'auto-p2');
  const south = cars.find((car) => car.name === 'auto-e2');
  if (!north || !south || bends.length !== 6) return new Map();
  const laneWidth = south.box.minZ - north.box.maxZ;
  const result = new Map(cars.map((car) => [car.name, { ...car }]));
  // Withdraw the old obstructions into their bays before dealing new ones.
  for (const { car, gap } of bends) {
    result.set(car.name, { ...car, boot: undefined,
      box: translated(car.box, 0, -car.boot!.end * (laneWidth - gap)) });
  }
  const gaps = shuffled(seed, 'parking:gaps', bends.map(({ gap }) => gap));
  const bays = choice(seed, 'parking:bays', SEED_LAYOUT.parkingBays);
  const firstSide = choice(seed, 'parking:side', SEED_LAYOUT.parkingSides);
  const pattern = choice(seed, 'parking:pattern', SEED_LAYOUT.parkingPatterns);
  bays.forEach((bay, i) => {
    const end = (pattern[i] * firstSide) as 1 | -1;
    const name = `auto-${end === 1 ? 'p' : 'e'}${bay}`;
    const car = result.get(name);
    if (!car) return;
    result.set(name, { ...car,
      box: translated(car.box, 0, end * (laneWidth - gaps[i])),
      boot: { ...bends[i].car.boot!, end },
    });
  });
  const identities = shuffled(seed, 'parking:cars', cars);
  cars.forEach((car, i) => {
    const target = result.get(car.name)!;
    result.set(car.name, { ...target, material: identities[i].material, plate: identities[i].plate });
  });
  return result;
}

/** Pure shared layout construction. Rendering and collision receive this same data. */
export function createSeededLevel(seed: number, base: LevelDefinition = TEST_LEVEL): LevelDefinition {
  if (!isSeed(seed)) throw new Error('Invalid level seed');
  if (seed === 0) return base;
  const parking = seededParking(seed, base);
  const mirrors = [1, 2].map((group) => choice(seed, `corridor:${group}`, SEED_LAYOUT.corridorMirror));
  const south = base.solids.find((solid) => solid.name === 'kaytava-seina-etela')?.box.maxZ;
  const north = base.solids.find((solid) => solid.name === 'kaytava-seina-pohjoinen')?.box.minZ;
  const corridorBox = (name: string, box: Box): Box => {
    const bed = /^kaytavan-(?:sanky|sangynrunko|patja)-(\d+)$/.exec(name);
    const gate = /^kaytavan-varustelaatikot-(\d+)(?:-sauma-\d+)?$/.exec(name);
    const group = bed ? Math.floor((Number(bed[1]) - 1) / 2) : gate ? Number(gate[1]) - 1 : -1;
    if (!mirrors[group] || south === undefined || north === undefined) return box;
    return { ...box, minZ: south + north - box.maxZ, maxZ: south + north - box.minZ };
  };
  const pallets = choice(seed, 'pallets', SEED_LAYOUT.palletOffsets);
  const exit = choice(seed, 'exit', SEED_LAYOUT.exits);
  const crossing = choice(seed, 'crossing', SEED_LAYOUT.crossings);
  const closure: SolidSpec = exit === 'window'
    ? { name: 'seed-kaytavan-varustepino', material: 'metal', box: { ...SEED_LAYOUT.corridorClosure } }
    : { name: 'seed-ikkunan-levy', material: 'wood', box: { ...SEED_LAYOUT.windowClosure } };
  return {
    ...base,
    ikkuna: { ...base.ikkuna, boarded: exit === 'corridor' },
    hotTub: { ...base.hotTub, hannuExitAngle: choice(seed, 'hannu:exit', SEED_LAYOUT.hannuExitAngles) },
    // An unloaded row offers a ground-level acceleration line. With the Audi
    // present but no stepping stones, its roof is above the ground jump apex
    // and the player must take the separate cable crossing to the south.
    solids: [...base.solids.filter((solid) =>
      (crossing === 'roof' || !/^ansalanka-harkot-[123]$/.test(solid.name)) &&
      (crossing !== 'gap' || solid.name !== 'ansalanka-auto-2')).map((solid) => {
      const car = parking.get(solid.name);
      if (car) return car;
      const pallet = /^ansalanka-harkot-([12])$/.exec(solid.name);
      const offset = pallet ? pallets[Number(pallet[1]) - 1] : undefined;
      const box = offset ? translated(solid.box, offset[0], offset[1]) : corridorBox(solid.name, solid.box);
      return box === solid.box ? solid : { ...solid, box };
    }), closure],
    decor: [...base.decor.map((item) => {
      const box = corridorBox(item.name, item.box);
      return box === item.box ? item : { ...item, box };
    }), ...(exit === 'window' ? SEED_LAYOUT.closureSeams.map((height, index) => ({
      name: `${closure.name}-sauma-${index}`, material: 'tyre' as const,
      box: { ...closure.box, minY: height, maxY: height + SEED_LAYOUT.closureSeamThickness,
        minX: closure.box.minX - SEED_LAYOUT.closureSeamOverhang,
        maxX: closure.box.maxX + SEED_LAYOUT.closureSeamOverhang,
        minZ: closure.box.minZ - SEED_LAYOUT.closureSeamOverhang,
        maxZ: closure.box.maxZ + SEED_LAYOUT.closureSeamOverhang },
    })) : [])],
    sleepers: base.sleepers.map((sleeper) => {
      if (!SEED_LAYOUT.sleepers.some((name) => name === sleeper.name)) return sleeper;
      const dz = choice(seed, `sleepers:${sleeper.name}`, SEED_LAYOUT.sleeperOffsets);
      return { ...sleeper, center: [sleeper.center[0], sleeper.center[1] + dz] };
    }),
  };
}
